
<script setup>

  import { ref } from 'vue';

  import { ArrowRightCircleIcon } from '@heroicons/vue/24/outline';

  import SimpleDialog from '../dialogs/simple.vue';

  import ParallaxLayer from '../components/parallax-layer.vue';

  import AudioPlayer from '../components/audio-player.vue';

  import { selectedRoute, selectedGas } from '../stores/activity-2.js';

  import audio_s1e1_1700_1755 from '../audio/s1e1_1700_1755.mp3';

  import track_s1e1_1700_1755 from '../audio/s1e1_1700_1755.vtt';

  import backgroundPath from '../images/rough-background.jpg';

  import layerPaths from '../images/activity-2-part-3-layer-*.png';

  const layers = Object.values( layerPaths );

  const stationDialog = ref( null );

  const bookDialog = ref( null );

  const noGasDialog = ref( null );

  selectedGas.value = null;

</script>

<template>

  <article>

    <div class="grid items-center justify-center min-h-view bg-fixed bg-cover bg-center"
    
      :style="{ backgroundImage: `url( ${ backgroundPath } )` }">

      <ParallaxLayer v-for="( path, index ) in layers" :z="30 - index * 10" class="row-start-1 col-start-1 w-screen h-full">

        <img :src="path" alt="" aria-hidden="true" class="w-full h-full object-cover scale-110 transition-[opacity,filter] grayscale opacity-10 xl:grayscale-0 xl:opacity-100" />

      </ParallaxLayer>

      <div class="row-start-1 col-start-1 relative p-6 md:p-12 lg:p-24">

        <div class="max-w-xl">

          <h3 class="text-5xl font-heading uppercase">

            You've been driving for a few miles

          </h3>

          <p class="mt-8 text-neutral-700">

            ...when you notice you're running low on gas and your extra gas can is not as full as you thought.
            
            Best to fill up now. What do you do? Find a service station in the Green Book? Or stop at the first station you see?

          </p>

          <div class="mt-12">

            <AudioPlayer :src="audio_s1e1_1700_1755" :track="track_s1e1_1700_1755" />
          
            <div class="mt-3 text-xs italic">Source: Driving the Green Book - Macmillan Podcasts - S1:E1</div>

          </div>

          <div class="md:flex items-center gap-6 mt-12">

            <button type="button" @click="event => { selectedGas = 'book'; selectedRoute === 'a' ? bookDialog.open() : noGasDialog.open() }"
            
              class="block px-6 py-4 font-bold text-white bg-green-700 transition-colors
              
              hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline">

              <div>Look in your Green Book.</div>

            </button>

            <div class="hidden md:block">Or</div>

            <button type="button" @click="event => { selectedGas = 'first'; stationDialog.open() }"
            
              class="block mt-6 md:mt-0 px-6 py-4 font-bold bg-amber-300 transition-colors
              
              hover:bg-amber-400 focus-visible:bg-amber-400 hover:underline focus-visible:underline">

              <div>Stop at first gas station you see.</div>

            </button>

          </div>

        </div>

      </div>

    </div>

    <SimpleDialog ref="stationDialog" title="Stop at the first gas station you see." class="max-w-2xl">

      <img src="../images/activity-2-part-3-station.jpg" alt="A vintage gas station with a sign that reads gas is available for 15 cents and 'Special Cigarettes' for 15 and 18 cents. A vintage vehicle and station is seen behind the sign." />

      <p class="mt-6 text-neutral-700">

        You stop at the first station you see without a "whites only" sign.

        The owner comes out, clearly agitated. He refuses to serve you.

        Thankfully, a passerby directs you to another station that will.

        You fill up your car and extra gas can, then continue on your way.

      </p>

      <div class="flex justify-end mt-6">

        <RouterLink
        
          class="inline-flex items-center gap-4 px-6 py-4 font-bold text-white bg-green-700 transition-colors
          
            hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline"
        
          to="/activity/2/part/4">

          <span>Continue</span>

          <ArrowRightCircleIcon class="w-8" />

        </RouterLink>

      </div>

    </SimpleDialog>

    <SimpleDialog ref="bookDialog" title="Look in your Green Book." class="max-w-2xl">

      <img src="../images/activity-2-part-3-book.jpg" alt="A page from the Green Book with service stations available. Three gas stations are listed and circled in red." />

      <p class="mt-6 text-neutral-700">

        You find a service station in D.C.

        It takes you a bit off course, but the station is next to Howard University, a historically Black college.

        You fill up your car and extra gas can, then continue on your way.

      </p>

      <div class="flex justify-end mt-6">

        <RouterLink
        
          class="inline-flex items-center gap-4 px-6 py-4 font-bold text-white bg-green-700 transition-colors
          
            hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline"
        
          to="/activity/2/part/4">

          <span>Continue</span>

          <ArrowRightCircleIcon class="w-8" />

        </RouterLink>

      </div>

    </SimpleDialog>

    <SimpleDialog ref="noGasDialog" title="No gas." class="max-w-2xl">

      <img src="../images/activity-2-part-3-no-gas.jpg" alt="A page from the Green Book with services available in Frederick, Maryland. A gas station is not listed." />

      <p class="mt-6 text-neutral-700">

        You check your Green Book, but no service stations are listed for Frederick or Harpers Ferry.

        You'll have to stop at the next station you see.

      </p>

      <div class="flex justify-end mt-6">

        <button type="button" @click="event => { noGasDialog.close(); stationDialog.open() }"
        
          class="inline-flex items-center gap-4 px-6 py-4 font-bold text-white bg-green-700 transition-colors
          
            hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline">

          <span>Continue</span>

          <ArrowRightCircleIcon class="w-8" />

        </button>

      </div>

    </SimpleDialog>

  </article>

</template>
